import React, { useEffect } from "react"
import { cn } from "../lib/utils"

interface NormalContentProps {
  content?: string
  tableOfContents: Array<{ id: string; title: string }>
  className?: string
}

const NormalContent: React.FC<NormalContentProps> = ({ content, tableOfContents, className }) => {
  if (!content) return null

  // Helper function to check if heading content is valid (not just &nbsp; or empty)
  const isValidHeadingContent = (headingContent: string): boolean => {
    // Remove HTML tags
    let text = headingContent.replace(/<[^>]+>/g, "").trim()

    // Decode HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&apos;/g, "'")
      .trim()

    // Check if empty or only whitespace
    return text !== "" && !/^[\s\u00A0]*$/.test(text)
  }

  let modifiedContent = content
  let tocIndex = 0

  modifiedContent = modifiedContent.replace(
    /<h([2-5])([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (match, level, attrs, innerContent) => {
      // Check if this heading has valid content
      if (!isValidHeadingContent(innerContent)) {
        // Return original heading without adding ID or incrementing index
        return match
      }

      // Get the corresponding TOC item
      const tocItem = tableOfContents[tocIndex]
      tocIndex += 1

      // Check if there's already an id attribute
      const hasId = /\sid=["'][^"']+["']/i.test(attrs)

      if (hasId || !tocItem) {
        return match
      }

      // Add id to the heading
      const newAttrs = attrs ? `${attrs} id="${tocItem.id}"` : ` id="${tocItem.id}"`
      return `<h${level}${newAttrs}>${innerContent}</h${level}>`
    },
  )

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const element = entry.target as HTMLElement
          if (entry.isIntersecting) {
            document.querySelector(`[href="#${element.id}"]`)?.classList.add("active")
          } else {
            document.querySelector(`[href="#${element.id}"]`)?.classList.remove("active")
          }
        })
      },
      {
        rootMargin: "-20% 0px -80% 0px",
      },
    )

    tableOfContents.forEach(({ id }) => {
      const element = document.getElementById(id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => {
      tableOfContents.forEach(({ id }) => {
        const element = document.getElementById(id)
        if (element) {
          observer.unobserve(element)
        }
      })
    }
  }, [tableOfContents])

  return (
    <div
      dangerouslySetInnerHTML={{ __html: modifiedContent }}
      className={cn(`sm:px-0 px-4 content-wrapper text-[#31343e]`, className)}
    />
  )
}

export default NormalContent
